import {
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core'
import { useState, useRef, useEffect, useCallback, memo } from 'react'
import { useHistory } from 'react-router'
import { formatPrice, formatToPercentage, safeDivision } from 'utils/format'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import StoresTableHead from '../StoresTable/components/StoresTableHead'
import { VisiblePerfectScrollbar } from './styles'
import { format } from 'date-fns'
import { BootstrapTooltip } from 'components/BootstrapTooltip'
import { UnavailableValue } from 'components/UnavailableValue'
import { dateWithNoTimezone } from 'utils/dateFunctions'
import { GoalBadge } from 'components/GoalBadge'
import { ConversionValue } from 'components/ConversionValue'
import { useInView } from 'react-intersection-observer'
import { getConversionRate } from 'utils/conversion'
import {
  FUTURE_SALES_MESSAGE,
  LAST_YEAR_UNAVAILABLE_MESSAGE,
  getAccessibleUnavailableProps,
  getDisabledFieldTooltipProps,
  getSalesDataDisabledReason,
  getSalesDisabledFieldTooltipProps,
  isAggregatedReportSortDisabled,
  isFieldDisabled
} from 'utils/reportDisabledFields'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%'
  },
  paper: {
    width: '100%'
  },
  table: {
    minWidth: 750
  },
  typographyPercent: {
    textAlign: 'right',
    fontSize: '11px',
    fontWeight: 'bold'
  },
  lastYearVariationText: {
    fontSize: '11px',
    fontWeight: 'bold'
  },
  sticky: {
    position: 'sticky',
    left: 0,
    background: 'white'
  }
}))

const GroupCard = memo(({ group, disabledFields, tableRef, onScroll }) => {
  const classes = useStyles()
  const history = useHistory()

  const [selectedRow, setSelectedRow] = useState(-1)
  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState('name')

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const acDayGoalReason = disabledFields.metric?.acDayGoal
  const mainGoalReason = disabledFields.metric?.mainGoal
  const projectionSoldReason = disabledFields.projection?.sold
  const salesDisabledReason = getSalesDataDisabledReason(disabledFields)
  const salesDisabled = isFieldDisabled(salesDisabledReason)
  const salesTooltipProps = getSalesDisabledFieldTooltipProps(disabledFields)
  const lastYearUnavailableMessage = salesDisabled
    ? FUTURE_SALES_MESSAGE
    : LAST_YEAR_UNAVAILABLE_MESSAGE
  const getStoreProjectionReason = store => projectionSoldReason ||
    (store.hasInputs === false ? 'NO_SALES_RECORDS' : undefined)

  const {
    name,
    supervisor,
    total,
    metric,
    daySold,
    dayGoals,
    average,
    projection,
    lastYearVariation,
    conversion
  } = group
  const groupConversionRate = getConversionRate(conversion)
  const showConversion = group.stores.some(store => store.conversion)

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1
    }
    if (b[orderBy] > a[orderBy]) {
      return 1
    }
    return 0
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index])
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0])
      if (order !== 0) return order
      return a[1] - b[1]
    })
    return stabilizedThis.map(el => el[0])
  }

  const handleRequestSort = property => {
    if (isAggregatedReportSortDisabled(property, disabledFields)) return

    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  useEffect(() => {
    if (isAggregatedReportSortDisabled(orderBy, disabledFields)) {
      setOrder('asc')
      setOrderBy('name')
    }
  }, [disabledFields, orderBy])

  const formattedStoresFromGroupsToStoresTable =
    group?.stores.length > 0
      ? group.stores.map(store => ({
          ...store,
          'average.tm': store.average.tm,
          'average.pv': store.average.pv,
          'average.pm': store.average.pm,
          'total.items': store.total.items,
          salesDay: store.daySold.sold,
          sold: store.total.sold,
          sales: store.total.sales,
          // Loja sem contagem vai para o fim na ordenação decrescente
          conversionRate: getConversionRate(store.conversion) ?? -1,
          items: store.total.items.toFixed(),
          goalPercent: salesDisabled || isFieldDisabled(acDayGoalReason)
            ? 0
            : Number(
              (safeDivision(store.total.sold, store.metric.acDayGoal) * 100)
                .toFixed(2)
            ),
          acDayGoal: store.metric.acDayGoal,
          mainGoal: store.metric.mainGoal,
          projection: store.hasInputs === false ? 0 : store.projection.sold,
          mainGoalPercent: salesDisabled || isFieldDisabled(mainGoalReason)
            ? 0
            : Number(
              (safeDivision(store.total.sold, store.metric.mainGoal) * 100)
                .toFixed(2)
            )
        }))
      : []

  const handleChangePage = newPage => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = rows => {
    setRowsPerPage(rows)
    setPage(0)
  }

  const getStoresForGroup = () => {
    const stores = stableSort(
      formattedStoresFromGroupsToStoresTable,
      getComparator(
        order,
        isAggregatedReportSortDisabled(orderBy, disabledFields)
          ? 'name'
          : orderBy
      )
    )

    const startIndex = page * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return stores.slice(startIndex, endIndex)
  }

  return (
    <Card>
      <CardHeader title={name} subheader={`Responsável: ${supervisor.name}`} />
      <Divider />
      <Paper className={classes.paper}>
        <TableContainer ref={tableRef} onScroll={onScroll}>
          <VisiblePerfectScrollbar>
            <Table
              stickyHeader
              onMouseLeave={() => {
                if (selectedRow > -1) {
                  setSelectedRow(-1)
                }
              }}
            >
              <StoresTableHead
                classes={classes}
                disabledFields={disabledFields}
                showConversion={showConversion}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                <TableRow
                  hover
                  key={1}
                  selected
                  onMouseEnter={() => {
                    if (selectedRow > -1) {
                      setSelectedRow(-1)
                    }
                  }}
                >
                  <TableCell
                    className={classes.sticky}
                    style={{ backgroundColor: '#fdf8ec' }}
                  >
                    <b>{name}</b>
                  </TableCell>
                  <TableCell {...salesTooltipProps}>
                    <b>{salesDisabled ? '-' : formatPrice(daySold.sold)}</b>
                  </TableCell>
                  <TableCell>
                    <b>{formatPrice(dayGoals.sell)}</b>
                  </TableCell>
                  <TableCell {...salesTooltipProps}>
                    <Box display='flex' alignItems='center' component='span'>
                      <b>{salesDisabled ? '-' : formatPrice(total.sold)}</b>
                    </Box>
                  </TableCell>
                  <TableCell {...(salesDisabled
                    ? salesTooltipProps
                    : getDisabledFieldTooltipProps(acDayGoalReason))}
                  >
                    <b>
                      {salesDisabled || isFieldDisabled(acDayGoalReason)
                        ? '-'
                        : formatToPercentage(
                            safeDivision(total.sold, metric.acDayGoal) * 100
                          )}
                    </b>
                  </TableCell>
                  <TableCell {...getDisabledFieldTooltipProps(acDayGoalReason)}>
                    <b>
                      {isFieldDisabled(acDayGoalReason)
                        ? '-'
                        : formatPrice(metric.acDayGoal)}
                    </b>
                  </TableCell>
                  <TableCell {...(salesDisabled
                    ? salesTooltipProps
                    : getDisabledFieldTooltipProps(mainGoalReason))}
                  >
                    <b>
                      {salesDisabled || isFieldDisabled(mainGoalReason)
                        ? '-'
                        : formatToPercentage(
                            safeDivision(total.sold, metric.mainGoal) * 100
                          )}
                    </b>
                  </TableCell>
                  <TableCell {...getDisabledFieldTooltipProps(mainGoalReason)}>
                    <b>
                      {isFieldDisabled(mainGoalReason)
                        ? '-'
                        : formatPrice(metric.mainGoal)}
                    </b>
                  </TableCell>
                  <TableCell {...getDisabledFieldTooltipProps(
                    projectionSoldReason || (group.hasInputs === false
                      ? 'NO_SALES_RECORDS'
                      : undefined)
                  )}>
                    <b>
                      {isFieldDisabled(projectionSoldReason) || group.hasInputs === false
                        ? '-'
                        : formatPrice(projection.sold)}
                    </b>
                  </TableCell>
                  {showConversion && (
                    <TableCell>
                      <ConversionValue
                        variant='badge'
                        conversion={conversion}
                        salesDisabled={salesDisabled}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <b>
                      {!salesDisabled && typeof lastYearVariation === 'number' ? (
                        <GoalBadge variation={lastYearVariation} />
                      ) : (
                        <UnavailableValue message={lastYearUnavailableMessage} />
                      )}
                    </b>
                  </TableCell>
                  <TableCell {...salesTooltipProps}>
                    {salesDisabled ? '-' : total ? total.sales : 0}
                  </TableCell>
                  <TableCell {...salesTooltipProps}>
                    <b>{salesDisabled ? '-' : formatPrice(average.tm)}</b>
                  </TableCell>
                  <TableCell {...salesTooltipProps}>
                    <b>{salesDisabled ? '-' : average.pv.toFixed(2)}</b>
                  </TableCell>
                  <TableCell {...salesTooltipProps}>
                    <b>{salesDisabled ? '-' : formatPrice(average.pm)}</b>
                  </TableCell>
                  <TableCell {...salesTooltipProps}>
                    <b>{salesDisabled ? '-' : total ? total.items : 0}</b>
                  </TableCell>
                </TableRow>

                {getStoresForGroup().map((store, index) => {
                  return (
                    <TableRow
                      hover
                      onClick={() => history.push(`/${store._id}/dashboard`)}
                      tabIndex={-1}
                      key={store._id}
                      style={{ cursor: 'pointer' }}
                      data-cy='store-row'
                      onMouseEnter={() => setSelectedRow(index)}
                    >
                      <TableCell
                        scope='row'
                        className={classes.sticky}
                        style={{
                          ...(index === selectedRow && {
                            backgroundColor: '#f5f5f5'
                          })
                        }}
                      >
                        {store.name}
                      </TableCell>
                      <TableCell
                        {...salesTooltipProps}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        {salesDisabled ? '-' : formatPrice(store.daySold.sold)}
                        {!salesDisabled && store.daySold.updatedAt && (
                          <BootstrapTooltip
                            title={`Ultima atualização: ${format(
                              dateWithNoTimezone(store.daySold.updatedAt),
                              'pp'
                            )}`}
                          >
                            <ErrorOutlineIcon
                              style={{
                                color: '#C4C4C4',
                                fontSize: 20,
                                marginLeft: 'auto'
                              }}
                            />
                          </BootstrapTooltip>
                        )}
                      </TableCell>
                      <TableCell>{formatPrice(store.dayGoals.sell)}</TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesDisabled ? '-' : formatPrice(store.total.sold)}
                      </TableCell>
                      <TableCell {...(salesDisabled
                        ? salesTooltipProps
                        : getDisabledFieldTooltipProps(acDayGoalReason))}
                      >
                        <Typography
                          style={{
                            color: salesDisabled || isFieldDisabled(acDayGoalReason)
                              ? 'inherit'
                              : (value =>
                                  value >= 100
                                    ? '#59ac2a'
                                    : value >= 80
                                    ? '#f0b222'
                                    : '#f34455')(
                                  safeDivision(
                                    store.total.sold,
                                    store.metric.acDayGoal
                                  ) * 100
                                )
                          }}
                          className={classes.typographyPercent}
                        >
                          {salesDisabled || isFieldDisabled(acDayGoalReason)
                            ? '-'
                            : store.metric.acDayGoal !== 0
                            ? +(
                                safeDivision(
                                  store.total.sold,
                                  store.metric.acDayGoal
                                ) * 100
                              ).toFixed(2) + '%'
                            : '0%'}
                        </Typography>
                      </TableCell>
                      <TableCell {...getDisabledFieldTooltipProps(acDayGoalReason)}>
                        {isFieldDisabled(acDayGoalReason)
                          ? '-'
                          : formatPrice(store.metric.acDayGoal)}
                      </TableCell>
                      <TableCell {...(salesDisabled
                        ? salesTooltipProps
                        : getDisabledFieldTooltipProps(mainGoalReason))}
                      >
                        <Typography
                          style={{
                            color: salesDisabled || isFieldDisabled(mainGoalReason)
                              ? 'inherit'
                              : (value =>
                                  value >= 100
                                    ? '#59ac2a'
                                    : value >= 80
                                    ? '#f0b222'
                                    : '#f34455')(
                                  safeDivision(
                                    store.total.sold,
                                    store.metric.mainGoal
                                  ) * 100
                                )
                          }}
                          className={classes.typographyPercent}
                        >
                          {salesDisabled || isFieldDisabled(mainGoalReason)
                            ? '-'
                            : store.metric.mainGoal !== 0
                            ? +(
                                safeDivision(
                                  store.total.sold,
                                  store.metric.mainGoal
                                ) * 100
                              ).toFixed(2) + '%'
                            : '0%'}
                        </Typography>
                      </TableCell>
                      <TableCell {...getDisabledFieldTooltipProps(mainGoalReason)}>
                        {isFieldDisabled(mainGoalReason)
                          ? '-'
                          : formatPrice(store.metric.mainGoal)}
                      </TableCell>
                      <TableCell {...getDisabledFieldTooltipProps(
                        getStoreProjectionReason(store)
                      )}>
                        {isFieldDisabled(getStoreProjectionReason(store))
                          ? '-'
                          : formatPrice(store.projection)}
                      </TableCell>
                      {showConversion && (
                        <TableCell>
                          <ConversionValue
                            conversion={store.conversion}
                            referenceRate={groupConversionRate}
                            salesDisabled={salesDisabled}
                          />
                        </TableCell>
                      )}
                      <TableCell {...(salesDisabled
                        ? salesTooltipProps
                        : typeof store.lastYearVariation !== 'number'
                          ? getAccessibleUnavailableProps(lastYearUnavailableMessage)
                          : {})}>
                        <Typography
                          style={{
                            color: !salesDisabled && typeof store.lastYearVariation === 'number'
                                ? (value =>
                                    value > 0
                                      ? '#59ac2a'
                                      : value > -10
                                      ? '#f0b222'
                                      : '#f34455')(store.lastYearVariation)
                                : '#757575'
                          }}
                          className={classes.lastYearVariationText}
                        >
                          {!salesDisabled && typeof store.lastYearVariation === 'number' ? formatToPercentage(store.lastYearVariation) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesDisabled ? '-' : store.total.sales}
                      </TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesDisabled ? '-' : formatPrice(store.average.tm)}
                      </TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesDisabled ? '-' : store.average.pv.toFixed(2)}
                      </TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesDisabled ? '-' : formatPrice(store.average.pm)}
                      </TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesDisabled ? '-' : store.total.items}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </VisiblePerfectScrollbar>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component='div'
          count={formattedStoresFromGroupsToStoresTable.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, page) => handleChangePage(page)}
          onRowsPerPageChange={event => {
            handleChangeRowsPerPage(Number(event.target.value))
          }}
        />
      </Paper>
    </Card>
  )
})

GroupCard.displayName = 'GroupCard'

const GroupCardWrapper = memo(({
  group,
  disabledFields,
  onVisibilityChange,
  onScroll
}) => {
  const { ref, inView } = useInView({ threshold: 0.1 })
  const tableContainerRef = useRef(null)

  const setRefs = useCallback(
    node => {
      tableContainerRef.current = node
      ref(node)
    },
    [ref]
  )

  useEffect(() => {
    onVisibilityChange(group.name, inView, tableContainerRef.current)
  }, [inView, group.name, onVisibilityChange, tableContainerRef])

  return (
    <GroupCard
      group={group}
      disabledFields={disabledFields}
      onScroll={onScroll}
      tableRef={setRefs}
    />
  )
})

GroupCardWrapper.displayName = 'GroupCardWrapper'

export const GroupsTable = ({ groups, disabledFields = {} }) => {
  const [activeTable, setActiveTable] = useState({ element: null, id: null })
  const activeTableRef = useRef({ element: null, id: null })
  const [visibleTables, setVisibleTables] = useState({})
  const fixedScrollbarRef = useRef(null)
  const [scrollbarWidth, setScrollbarWidth] = useState(0)
  const isSyncing = useRef(false)

  const handleVisibilityChange = useCallback((id, inView, element) => {
    setVisibleTables(prev => ({
      ...prev,
      [id]: { inView, element }
    }))
  }, [])

  useEffect(() => {
    let lastVisibleTable = null
    let maxTop = -Infinity

    Object.keys(visibleTables).forEach(id => {
      const table = visibleTables[id]
      if (table.inView && table.element) {
        const { top } = table.element.getBoundingClientRect()
        if (top > maxTop) {
          maxTop = top
          lastVisibleTable = { element: table.element, id }
        }
      }
    })

    if (lastVisibleTable) {
      setActiveTable(lastVisibleTable)
      activeTableRef.current = lastVisibleTable
    } else {
      const firstVisible = Object.values(visibleTables).find(v => v.inView)
      if (!firstVisible) {
        const newActiveTable = { element: null, id: null }
        setActiveTable(newActiveTable)
        activeTableRef.current = newActiveTable
      }
    }
  }, [visibleTables])

  useEffect(() => {
    if (!activeTable.element) {
      setScrollbarWidth(0)
      if (fixedScrollbarRef.current) {
        fixedScrollbarRef.current.scrollLeft = 0
      }
      return
    }

    const tableContent = activeTable.element.firstChild
    if (!tableContent) return

    const calculateAndUpdateScrollbarWidth = target => {
      const tableScrollWidth = target.scrollWidth
      const tableClientWidth = target.clientWidth
      const fixedClientWidth = fixedScrollbarRef.current?.clientWidth

      if (fixedClientWidth) {
        const newScrollbarWidth =
          tableScrollWidth - tableClientWidth + fixedClientWidth
        setScrollbarWidth(newScrollbarWidth)
      } else {
        setScrollbarWidth(tableScrollWidth)
      }
    }

    const resizeObserver = new ResizeObserver(entries => {
      // Using requestAnimationFrame to avoid ResizeObserver loop error
      requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return
        }
        calculateAndUpdateScrollbarWidth(entries[0].target)
      })
    })

    resizeObserver.observe(tableContent)
    calculateAndUpdateScrollbarWidth(tableContent)

    if (fixedScrollbarRef.current) {
      // Sync scrollbar position to the newly active table's scroll position
      const scrollableContent = activeTable.element.firstChild
      if (scrollableContent) {
        fixedScrollbarRef.current.scrollLeft = scrollableContent.scrollLeft
      }
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [activeTable.element])

  const handleFixedScroll = useCallback(() => {
    if (isSyncing.current) return
    if (activeTableRef.current.element && fixedScrollbarRef.current) {
      const scrollableContent = activeTableRef.current.element.firstChild
      if (scrollableContent) {
        isSyncing.current = true
        scrollableContent.scrollLeft = fixedScrollbarRef.current.scrollLeft
        requestAnimationFrame(() => {
          isSyncing.current = false
        })
      }
    }
  }, [])

  const handleTableScroll = useCallback(
    e => {
      if (isSyncing.current) return
      if (
        activeTableRef.current.element &&
        activeTableRef.current.element.contains(e.target) &&
        fixedScrollbarRef.current
      ) {
        isSyncing.current = true
        fixedScrollbarRef.current.scrollLeft = e.target.scrollLeft
        requestAnimationFrame(() => {
          isSyncing.current = false
        })
      }
    },
    []
  )

  return (
    <>
      {groups.map((group, index) => (
        <Grid
          item
          xs={12}
          key={group.name}
          style={{
            ...(groups.length > index + 1 && {
              paddingBottom: '32px'
            })
          }}
        >
          <GroupCardWrapper
            group={group}
            disabledFields={disabledFields}
            onVisibilityChange={handleVisibilityChange}
            onScroll={handleTableScroll}
          />
        </Grid>
      ))}
      <VisiblePerfectScrollbar
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '15px',
          zIndex: 1200,
          visibility: activeTable.element ? 'visible' : 'hidden'
        }}
        onScroll={handleFixedScroll}
        containerRef={el => {
          fixedScrollbarRef.current = el
        }}
      >
        <div style={{ width: scrollbarWidth, height: '1px' }} />
      </VisiblePerfectScrollbar>
    </>
  )
}
